import type { FC, ReactNode } from 'react'
import { createContext, useState, useContext, useMemo } from 'react'

import { useMutation } from '@tanstack/react-query'

import type {
  EndOfLeaseAssetsValidate,
  ExtendAssetsValidate,
  ReturnAssetsValidate,
} from '@/resources/leasing'
import { leasingResources } from '@/resources/leasing'

import type { LeasedAsset } from '../resources'

type AssetsLeasedContextType = {
  selectedList: number[]
  unavailableAssets: EndOfLeaseAssetsValidate | ExtendAssetsValidate | ReturnAssetsValidate
  action: string | number | null
  isActionPopupOpen: boolean

  setSelectedList: (list: number[]) => void
  setUnavailableAssets: (
    list: EndOfLeaseAssetsValidate | ExtendAssetsValidate | ReturnAssetsValidate
  ) => void
  setAction: (name: string | number | null) => void
  setIsActionPopupOpen: (open: boolean) => void

  clearSelectedList: () => void
  toggleSelect: (id: number) => void
  selectAll: (visibleListAssets: LeasedAsset[]) => void
  handleActionChange: (value: string | number) => void
  onConfirmClick: () => void
  resetMutations: () => void

  isLoadingValidation: boolean
  isErrorValidation: boolean
  isSuccessValidation: boolean
  isLoadingConfirm: boolean
  isErrorConfirm: boolean
  isSuccessConfirm: boolean
}

const AssetsLeasedContext = createContext<AssetsLeasedContextType | undefined>(undefined)

export const useLeasedAssets = () => {
  const context = useContext(AssetsLeasedContext)
  if (!context) {
    throw new Error('useLeased must be used within an AssetsLeasedProvider')
  }
  return context
}

export const AssetsLeasedProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedList, setSelectedList] = useState<number[]>([])
  const [unavailableAssets, setUnavailableAssets] = useState<
    EndOfLeaseAssetsValidate | ExtendAssetsValidate | ReturnAssetsValidate
  >([])
  const [action, setAction] = useState<string | number | null>(null)
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false)

  const isActionExtend = Number.isFinite(action)
  const isEndOfLeaseAction = action === 'EndOfLease'

  const clearSelectedList = () => setSelectedList([])
  const toggleSelect = (id: number) => {
    const list = selectedList.includes(id)
      ? selectedList.filter(el => el !== id)
      : [...selectedList, id]
    setSelectedList(list)
  }
  const selectAll = (assetsList: LeasedAsset[]) => {
    const list = assetsList.length === selectedList.length ? [] : assetsList.map(el => el.id)
    setSelectedList(list)
  }

  const {
    mutate: validateEOLAssetMutation,
    isLoading: isLoadingValidateEOL,
    isError: isErrorValidateEOL,
    isSuccess: isSuccessValidateEOL,
    reset: resetValidateEOLAsset,
  } = useMutation(leasingResources.endOfLeaseAssetsValidate, {
    onSuccess(data) {
      setUnavailableAssets(data)
    },
  })
  const {
    isLoading: isLoadingEOL,
    isError: isErrorEOL,
    isSuccess: isSuccessEOL,
    mutate: eolAssetMutation,
    reset: resetEOLAsset,
  } = useMutation(leasingResources.endOfLeaseAssets, {
    onSuccess() {
      setUnavailableAssets([])
    },
  })

  const {
    mutate: validateReturnAssetMutation,
    isLoading: isLoadingValidateReturn,
    isError: isErrorValidateReturn,
    isSuccess: isSuccessValidateReturn,
    reset: resetValidateReturnAsset,
  } = useMutation(leasingResources.returnAssetsValidate, {
    onSuccess(data) {
      setUnavailableAssets(data)
    },
  })
  const {
    isLoading: isLoadingReturn,
    isError: isErrorReturn,
    isSuccess: isSuccessReturn,
    mutate: returnAssetMutation,
    reset: resetReturnAsset,
  } = useMutation(leasingResources.returnAssets, {
    onSuccess() {
      setUnavailableAssets([])
    },
  })

  const {
    mutate: validateExtendAssetMutation,
    isLoading: isLoadingValidateExtend,
    isError: isErrorValidateExtend,
    isSuccess: isSuccessValidateExtend,
    reset: resetValidateExtendAsset,
  } = useMutation(leasingResources.extendAssetsValidate, {
    onSuccess(data) {
      setUnavailableAssets(data)
    },
  })
  const {
    isLoading: isLoadingExtend,
    isError: isErrorExtend,
    isSuccess: isSuccessExtend,
    mutate: extendAssetMutation,
    reset: resetExtendAsset,
  } = useMutation(leasingResources.extendAssets, {
    onSuccess() {
      setUnavailableAssets([])
    },
  })

  const handleActionChange = (value: string | number) => {
    setAction(value)
    setIsActionPopupOpen(true)
    if (value === 'EndOfLease') {
      const payload = { assetIds: selectedList }
      validateEOLAssetMutation(payload)
    } else if (value === 'RETURN') {
      const payload = { assetIds: selectedList }
      validateReturnAssetMutation(payload)
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      const payload = { assetIds: selectedList, numberOfMonths: value }
      validateExtendAssetMutation(payload)
    }
  }

  const onConfirmClick = () => {
    if (action === 'EndOfLease') {
      const payload = { assetIds: selectedList }
      eolAssetMutation(payload)
    } else if (action === 'RETURN') {
      const payload = { assetIds: selectedList }
      returnAssetMutation(payload)
    } else if (typeof action === 'number' && Number.isFinite(action)) {
      const payload = { assetIds: selectedList, numberOfMonths: action }
      extendAssetMutation(payload)
    }
  }

  const getValidationStatus = () => {
    if (isActionExtend) {
      return {
        isLoadingValidation: isLoadingValidateExtend,
        isErrorValidation: isErrorValidateExtend,
        isSuccessValidation: isSuccessValidateExtend,
      }
    }
    if (isEndOfLeaseAction) {
      return {
        isLoadingValidation: isLoadingValidateEOL,
        isErrorValidation: isErrorValidateEOL,
        isSuccessValidation: isSuccessValidateEOL,
      }
    }
    // RETURN action
    return {
      isLoadingValidation: isLoadingValidateReturn,
      isErrorValidation: isErrorValidateReturn,
      isSuccessValidation: isSuccessValidateReturn,
    }
  }

  const { isLoadingValidation, isErrorValidation, isSuccessValidation } = getValidationStatus()

  const getConfirmStatus = () => {
    if (isActionExtend) {
      return {
        isLoadingConfirm: isLoadingExtend,
        isErrorConfirm: isErrorExtend,
        isSuccessConfirm: isSuccessExtend,
      }
    }
    if (isEndOfLeaseAction) {
      return {
        isLoadingConfirm: isLoadingEOL,
        isErrorConfirm: isErrorEOL,
        isSuccessConfirm: isSuccessEOL,
      }
    }
    // RETURN action
    return {
      isLoadingConfirm: isLoadingReturn,
      isErrorConfirm: isErrorReturn,
      isSuccessConfirm: isSuccessReturn,
    }
  }
  const { isLoadingConfirm, isErrorConfirm, isSuccessConfirm } = getConfirmStatus()

  const resetMutations = () => {
    if (isLoadingValidation || isErrorValidation || isSuccessValidation) {
      resetValidateEOLAsset()
      resetValidateExtendAsset()
      resetValidateReturnAsset()
    }
    if (isLoadingConfirm || isErrorConfirm || isSuccessConfirm) {
      resetEOLAsset()
      resetExtendAsset()
      resetReturnAsset()
    }
  }

  const value: AssetsLeasedContextType = useMemo(
    () => ({
      selectedList,
      unavailableAssets,
      action,
      isActionPopupOpen,
      isLoadingValidation,
      isErrorValidation,
      isSuccessValidation,
      isLoadingConfirm,
      isErrorConfirm,
      isSuccessConfirm,
      setSelectedList,
      setUnavailableAssets,
      setAction,
      setIsActionPopupOpen,
      clearSelectedList,
      toggleSelect,
      selectAll,
      handleActionChange,
      onConfirmClick,
      resetMutations,
    }),
    [
      action,
      isActionPopupOpen,
      selectedList,
      unavailableAssets,
      isLoadingValidation,
      isErrorValidation,
      isSuccessValidation,
      isLoadingConfirm,
      isErrorConfirm,
      isSuccessConfirm,
    ]
  )
  return <AssetsLeasedContext.Provider value={value}>{children}</AssetsLeasedContext.Provider>
}
